import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BrandingContext = createContext(null);

// Hex → "h s% l%" (for Tailwind/Shadcn hsl()-wrapped tokens)
function hexToHsl(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyBranding(branding) {
  const root = document.documentElement;
  if (branding?.primary_color) {
    const hsl = hexToHsl(branding.primary_color);
    if (hsl) root.style.setProperty("--primary", hsl);
  } else {
    root.style.removeProperty("--primary");
  }
  if (branding?.accent_color) {
    const hsl = hexToHsl(branding.accent_color);
    if (hsl) root.style.setProperty("--accent", hsl);
  } else {
    root.style.removeProperty("--accent");
  }
  // Dynamic favicon + page title
  const title = branding?.display_name || "DATON";
  document.title = `${title} — IT-Support & Kundenverwaltung`;
  const favicon = document.querySelector("link[rel='icon']") || document.createElement("link");
  favicon.rel = "icon";
  if (branding?.logo_data_uri) {
    favicon.type = branding.logo_data_uri.match(/data:(image\/[^;]+)/)?.[1] || "image/png";
    favicon.href = branding.logo_data_uri;
  } else {
    favicon.type = "image/svg+xml";
    favicon.href = "/favicon.svg";
  }
  if (!favicon.parentElement) document.head.appendChild(favicon);
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);

  const reload = useCallback(async () => {
    if (!user || user.is_platform_admin) {
      setBranding(null); applyBranding(null); return;
    }
    try {
      const r = await axios.get(`${API}/tenants/branding/me`, { withCredentials: true });
      setBranding(r.data);
      applyBranding(r.data);
    } catch { /* ignore — keep defaults */ }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <BrandingContext.Provider value={{ branding, reload, setBranding: (b) => { setBranding(b); applyBranding(b); } }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext) || { branding: null };
}
